/**
 * Detalle de un movimiento de cuenta.
 *
 * Portado del modal `#staticBackdrop` de `views/bank_accounts/movements.php` y de
 * `drawPayment()` en `src/bank_accounts/movements.js`: los datos del movimiento
 * según su tipo —cliente o proveedor, comisión de las visas, descripción de los
 * ingresos y egresos, control de efectivo de los depósitos— y, cuando los trae, el
 * **reparto** del pago (las facturas con lo aplicado y su saldo) y los **cobros**
 * que componen una visa POS.
 *
 * El botón de anular sólo aparece si el tipo se puede anular y el usuario tiene
 * rol de gestión, que es la condición del original.
 */

import { AlertTriangle, Ban, FileText, Paperclip } from 'lucide-react';
import { useEffect } from 'react';

import { Alert, ICONO_EN_ALERTA } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Dialog } from '@/components/ui/dialog';
import { Skeleton } from '@/components/ui/skeleton';
import { abrirPdfAutenticado } from '@/lib/descargas';
import { ApiError } from '@/lib/http/api-error';
import { cn, formatCurrency, formatDateValue } from '@/lib/utils';

import { movimientosApi, useMovimientoDetalle } from '../movimientos.api';
import type { MovimientoCuenta } from '../movimientos.types';

interface MovimientoDetalleDialogProps {
  movimiento: MovimientoCuenta | null;
  onClose: () => void;
  onAnular: (movimiento: MovimientoCuenta) => void;
}

/** Una línea de la ficha: etiqueta a la izquierda y valor a la derecha. */
function Dato({ etiqueta, valor }: { etiqueta: string; valor: React.ReactNode }) {
  return (
    <div className="flex flex-wrap items-baseline justify-between gap-2 border-b border-ink-50 py-2 last:border-0">
      <span className="text-xs font-semibold tracking-wide text-ink-500 uppercase">
        {etiqueta}
      </span>
      <span className="text-sm text-ink-900">{valor}</span>
    </div>
  );
}

export function MovimientoDetalleDialog({
  movimiento,
  onClose,
  onAnular,
}: MovimientoDetalleDialogProps) {
  const consulta = useMovimientoDetalle(movimiento?.id ?? null);
  const detalle = consulta.data;

  // El detalle se vuelve a pedir al abrir otro movimiento.
  useEffect(() => {
    if (movimiento) void consulta.refetch();
    // Sólo al cambiar de movimiento: `refetch` cambia de identidad en cada render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [movimiento?.id]);

  const abrirComprobante = async () => {
    if (!movimiento) return;

    try {
      await abrirPdfAutenticado(movimientosApi.urlComprobante(movimiento.id));
    } catch (fallo) {
      console.error('[movimientos] No se pudo abrir el comprobante:', fallo);
    }
  };

  const datos = detalle?.movimiento ?? movimiento;
  const comision = datos?.comision ?? null;

  return (
    <Dialog
      open={Boolean(movimiento)}
      onClose={onClose}
      title={`Movimiento #${movimiento?.id ?? ''}`}
      description={
        movimiento
          ? `${movimiento.tipo} · ${movimiento.banco.trim()} · ${movimiento.cuenta}`
          : undefined
      }
      size="xl"
      footer={
        <div className="flex w-full flex-wrap items-center justify-between gap-2">
          <div>
            {datos?.puede_anular && movimiento ? (
              <Button
                variant="danger"
                size="sm"
                leftIcon={<Ban className="size-3.5" aria-hidden="true" />}
                onClick={() => onAnular(movimiento)}
              >
                Anular movimiento
              </Button>
            ) : null}
          </div>

          <div className="flex flex-wrap items-center gap-2">
            {datos?.comprobante ? (
              <Button
                variant="outline"
                size="sm"
                leftIcon={<Paperclip className="size-3.5" aria-hidden="true" />}
                onClick={() => void abrirComprobante()}
              >
                Ver comprobante
              </Button>
            ) : null}

            <Button variant="outline" size="sm" onClick={onClose}>
              Cerrar
            </Button>
          </div>
        </div>
      }
    >
      {consulta.isLoading ? (
        <div className="space-y-2">
          <Skeleton className="h-6 w-2/3" />
          <Skeleton className="h-24 w-full" />
          <Skeleton className="h-24 w-full" />
        </div>
      ) : consulta.isError ? (
        <Alert variant="error" title="No se pudo cargar el detalle">
          {consulta.error instanceof ApiError
            ? consulta.error.message
            : 'Inténtalo de nuevo en unos segundos.'}
        </Alert>
      ) : datos ? (
        <div className="space-y-5">
          {movimiento?.anulado_por ? (
            <Alert variant="warning" title="Movimiento anulado">
              Lo anuló {movimiento.anulado_por}
              {movimiento.anulado_el
                ? ` el ${formatDateValue(movimiento.anulado_el.slice(0, 10))}`
                : ''}
              .
            </Alert>
          ) : null}

          <section className="grid gap-x-8 gap-y-0 sm:grid-cols-2">
            <div>
              <Dato etiqueta="Tipo" valor={<Badge variant="brand">{datos.tipo}</Badge>} />
              {datos.subtipo ? <Dato etiqueta="Subtipo" valor={datos.subtipo} /> : null}
              <Dato etiqueta="Dirección" valor={datos.direccion} />
              <Dato etiqueta="Fecha del movimiento" valor={formatDateValue(datos.fecha)} />
              <Dato etiqueta="Registrado" valor={datos.registrado ?? '—'} />
              <Dato etiqueta="Registró" valor={datos.usuario ?? '—'} />
            </div>

            <div>
              <Dato etiqueta="Banco" valor={datos.banco.trim()} />
              <Dato etiqueta="Cuenta" valor={datos.cuenta} />
              <Dato etiqueta="Método de pago" valor={datos.metodo_pago ?? '—'} />
              {datos.turno_id ? (
                <Dato etiqueta="Turno" valor={`#${datos.turno_id}`} />
              ) : null}
              <Dato
                etiqueta="Monto"
                valor={
                  <span
                    className={cn(
                      'font-bold tabular-nums',
                      datos.direccion === 'EGRESO' ? 'text-rose-600' : 'text-emerald-600',
                    )}
                  >
                    {datos.direccion === 'EGRESO' ? '−' : '+'} {formatCurrency(datos.monto_absoluto)}
                  </span>
                }
              />
              {comision ? (
                <>
                  <Dato
                    etiqueta={`Comisión (${comision.porcentaje}%)`}
                    valor={formatCurrency(comision.monto)}
                  />
                  <Dato etiqueta="Monto neto" valor={formatCurrency(comision.neto)} />
                </>
              ) : null}
            </div>
          </section>

          {/* Datos del cliente, proveedor, operador o descripción del movimiento. */}
          {datos.cliente || datos.documento || datos.referencia ? (
            <section className="rounded-card border border-ink-100 p-4">
              <h3 className="text-sm font-semibold text-ink-800">Datos del movimiento</h3>
              <div className="mt-1">
                {datos.cliente ? <Dato etiqueta="Cliente / Proveedor" valor={datos.cliente} /> : null}
                {datos.documento ? <Dato etiqueta="Documento" valor={datos.documento} /> : null}
                {datos.referencia ? <Dato etiqueta="Referencia" valor={datos.referencia} /> : null}
              </div>
            </section>
          ) : null}

          {datos.observaciones ? (
            <section className="rounded-card border border-ink-100 p-4">
              <h3 className="text-sm font-semibold text-ink-800">Observaciones</h3>
              <p className="mt-2 flex items-start gap-2 text-sm text-ink-600">
                <FileText className="mt-0.5 size-3.5 shrink-0 text-ink-400" aria-hidden="true" />
                <span>{datos.observaciones}</span>
              </p>
            </section>
          ) : null}

          {/* Reparto del pago: las facturas que se cubrieron con este movimiento. */}
          {detalle && detalle.dispersion.length > 0 ? (
            <section>
              <h3 className="mb-2 text-sm font-semibold text-ink-800">
                Facturas pagadas ({detalle.dispersion.length})
              </h3>

              <div className="overflow-auto rounded-card border border-ink-100">
                <table className="w-full border-collapse text-[13px]">
                  <thead className="bg-white shadow-[0_1px_0_0_var(--color-ink-100)]">
                    <tr className="text-left text-[11px] font-semibold tracking-wide text-ink-500 uppercase">
                      <th className="px-2 py-2">Factura</th>
                      <th className="px-2 py-2 text-right">Valor factura</th>
                      <th className="px-2 py-2 text-right">Monto aplicado</th>
                      <th className="px-2 py-2 text-right">Saldo pendiente</th>
                    </tr>
                  </thead>
                  <tbody>
                    {detalle.dispersion.map((linea) => (
                      <tr key={`${linea.venta_id}-${linea.factura}`} className="border-t border-ink-100">
                        <td className="px-2 py-2 font-semibold text-ink-800">{linea.factura}</td>
                        <td className="px-2 py-2 text-right tabular-nums">
                          {formatCurrency(linea.valor_factura)}
                        </td>
                        <td className="px-2 py-2 text-right tabular-nums">
                          {formatCurrency(linea.monto)}
                        </td>
                        <td
                          className={cn(
                            'px-2 py-2 text-right font-semibold tabular-nums',
                            linea.saldo > 0 ? 'text-amber-700' : 'text-emerald-700',
                          )}
                        >
                          {formatCurrency(linea.saldo)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </section>
          ) : null}

          {/* Cobros que componen una visa POS. */}
          {detalle && detalle.cobros.length > 0 ? (
            <section>
              <h3 className="mb-2 text-sm font-semibold text-ink-800">
                Cobros que componen el depósito ({detalle.cobros.length})
              </h3>

              <div className="overflow-auto rounded-card border border-ink-100">
                <table className="w-full border-collapse text-[13px]">
                  <thead className="bg-white shadow-[0_1px_0_0_var(--color-ink-100)]">
                    <tr className="text-left text-[11px] font-semibold tracking-wide text-ink-500 uppercase">
                      <th className="px-2 py-2">Documento</th>
                      <th className="px-2 py-2">Cliente</th>
                      <th className="px-2 py-2 text-right">Bruto</th>
                      <th className="px-2 py-2 text-right">Comisión</th>
                      <th className="px-2 py-2 text-right">Neto</th>
                    </tr>
                  </thead>
                  <tbody>
                    {detalle.cobros.map((cobro, indice) => (
                      <tr key={`${cobro.documento}-${indice}`} className="border-t border-ink-100">
                        <td className="px-2 py-2 font-semibold text-ink-800">{cobro.documento || '—'}</td>
                        <td className="px-2 py-2 text-ink-600">{cobro.cliente || '—'}</td>
                        <td className="px-2 py-2 text-right tabular-nums">
                          {formatCurrency(cobro.bruto)}
                        </td>
                        <td className="px-2 py-2 text-right tabular-nums text-rose-600">
                          {formatCurrency(cobro.comision)}
                        </td>
                        <td className="px-2 py-2 text-right font-semibold tabular-nums text-emerald-700">
                          {formatCurrency(cobro.neto)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </section>
          ) : null}

          {movimiento && !movimiento.anulable ? (
            <p className={ICONO_EN_ALERTA}>
              <AlertTriangle className="size-3.5" aria-hidden="true" />
              Los movimientos de tipo {movimiento.tipo} se anulan desde su propia pantalla.
            </p>
          ) : null}
        </div>
      ) : null}
    </Dialog>
  );
}
